import React, { useState, useRef, useCallback } from 'react';

interface UseInactivityModalProps {
  isCartNotEmpty: boolean;
  onExit: () => void;
  inactivityTimeout?: number;
}

export function useInactivityModal({ isCartNotEmpty, onExit, inactivityTimeout }: UseInactivityModalProps) {
  const timerRef = useRef<number  | null>(null);
  const [state, setState] = useState({
    open: false,
    modalKey: 0,
  });

  const clearInactivityTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearInactivityTimer();
    if (isCartNotEmpty) {
      timerRef.current = window.setTimeout(() => {
        setState((s) => ({ ...s, open: true, modalKey: s.modalKey + 1 }));
      }, inactivityTimeout);
    }
  }, [isCartNotEmpty, inactivityTimeout, clearInactivityTimer]);

  React.useEffect(() => {
    resetTimer();
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    const handler = () => resetTimer();
    events.forEach((e) => window.addEventListener(e, handler));
    return () => {
      clearInactivityTimer();
      events.forEach((e) => window.removeEventListener(e, handler));
    };
  }, [resetTimer, clearInactivityTimer]);

  const handleContinue = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    resetTimer();
  }, [resetTimer]);

  const handleExit = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    clearInactivityTimer();
    onExit();
  }, [onExit, clearInactivityTimer]);

  const handleModalClose = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    clearInactivityTimer();
  }, [clearInactivityTimer]);

  return {
    inactivityOpen: state.open,
    inactivityModalKey: state.modalKey,
    handleContinue,
    handleExit,
    handleModalClose,
    resetTimer,
  };
}
